import React, { useState } from 'react';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import {format} from 'date-fns';

const {FiActivity, FiAlertTriangle, FiMapPin, FiExternalLink, FiClock, FiInfo, FiList} = FiIcons;

const IliganScorecard = ({calamities, onAlertClick, onShowEvents}) => {
  // Filter calamities for Iligan City (within 100km instead of 50km)
  const iliganCalamities = calamities.filter(c => 
    c.nearest_city?.name === 'Iligan City' || 
    (c.nearest_city?.distance_km && c.nearest_city.distance_km <= 100)
  );

  // Categorize events
  const earthquakeEvents = iliganCalamities.filter(c => c.type === 'earthquake');
  const volcanicEvents = iliganCalamities.filter(c => c.type === 'volcanic');
  const floodEvents = iliganCalamities.filter(c => c.type === 'flood');
  const tidalEvents = iliganCalamities.filter(c => c.type === 'tidal');
  const weatherEvents = iliganCalamities.filter(c => c.type === 'weather');
  const tsunamiEvents = iliganCalamities.filter(c => c.type === 'tsunami');

  // Get recent events (last 7 days)
  const oneWeekAgo = new Date();
  oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
  
  const recentEarthquakes = earthquakeEvents.filter(e => new Date(e.time_utc) > oneWeekAgo);
  const recentVolcanic = volcanicEvents.filter(e => new Date(e.time_utc) > oneWeekAgo);
  const recentFloods = floodEvents.filter(e => new Date(e.time_utc) > oneWeekAgo);
  const recentTides = tidalEvents.filter(e => new Date(e.time_utc) > oneWeekAgo);
  const recentWeather = weatherEvents.filter(e => new Date(e.time_utc) > oneWeekAgo);
  const recentTsunamis = tsunamiEvents.filter(e => new Date(e.time_utc) > oneWeekAgo);

  // Get alert levels
  const earthquakeAlerts = recentEarthquakes.filter(e => e.alert !== 'None');
  const volcanicAlerts = recentVolcanic.filter(e => e.alert !== 'None');
  const floodAlerts = recentFloods.filter(e => e.alert !== 'None');
  const weatherAlerts = recentWeather.filter(e => e.alert !== 'None');
  const tsunamiAlerts = recentTsunamis.filter(e => e.alert !== 'None');

  // Get the latest tidal event
  const latestTide = recentTides.length > 0 
    ? recentTides.reduce((latest, current) => 
        new Date(current.time_utc) > new Date(latest.time_utc) ? current : latest
      )
    : null;

  const hazardData = [
    {
      type: 'Earthquakes',
      icon: '🌍',
      count: recentEarthquakes.length,
      alerts: earthquakeAlerts.length,
      color: 'bg-red-100 text-red-800',
      alertColor: 'bg-red-500',
      alertEvents: earthquakeAlerts,
      allEvents: recentEarthquakes,
      description: 'Seismic activity including main shocks and aftershocks'
    },
    {
      type: 'Volcanic',
      icon: '🌋',
      count: recentVolcanic.length,
      alerts: volcanicAlerts.length,
      color: 'bg-orange-100 text-orange-800',
      alertColor: 'bg-orange-500',
      alertEvents: volcanicAlerts,
      allEvents: recentVolcanic,
      description: 'Volcanic eruptions and related activity'
    },
    {
      type: 'Floods',
      icon: '🌊',
      count: recentFloods.length,
      alerts: floodAlerts.length,
      color: 'bg-blue-100 text-blue-800',
      alertColor: 'bg-blue-500',
      alertEvents: floodAlerts,
      allEvents: recentFloods,
      description: 'River and flash flooding events'
    },
    {
      type: 'Tides',
      icon: latestTide ? (latestTide.event_type === 'high_tide' ? '⬆️' : '⬇️') : '📊',
      count: recentTides.length,
      alerts: 0,
      color: 'bg-cyan-100 text-cyan-800',
      alertColor: 'bg-cyan-500',
      alertEvents: [],
      allEvents: recentTides,
      tideInfo: latestTide ? {
        type: latestTide.event_type,
        time: format(new Date(latestTide.time_utc), 'MMM dd, h:mm a')
      } : null,
      description: 'High and low tide events affecting coastal areas'
    },
    {
      type: 'Weather',
      icon: '🌀',
      count: recentWeather.length,
      alerts: weatherAlerts.length,
      color: 'bg-purple-100 text-purple-800',
      alertColor: 'bg-purple-500',
      alertEvents: weatherAlerts,
      allEvents: recentWeather,
      description: 'Severe weather including tropical depressions and monsoons'
    },
    {
      type: 'Tsunamis',
      icon: '🌊',
      count: recentTsunamis.length,
      alerts: tsunamiAlerts.length,
      color: 'bg-red-200 text-red-900',
      alertColor: 'bg-red-600',
      alertEvents: tsunamiAlerts,
      allEvents: recentTsunamis,
      description: 'Tsunami warnings and advisories'
    }
  ];

  return (
    <motion.div 
      initial={{opacity: 0, y: -20}}
      animate={{opacity: 1, y: 0}}
      className="bg-white rounded-lg shadow-md p-6 mb-6 border-l-4 border-blue-500"
    >
      <div className="flex items-center space-x-2 mb-4">
        <SafeIcon icon={FiMapPin} className="text-blue-600 text-xl" />
        <h2 className="text-xl font-bold text-gray-900">Iligan City Hazard Summary</h2>
        <span className="text-sm text-gray-500">(100km radius)</span>
      </div>
      
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4">
        {hazardData.map((hazard, index) => (
          <motion.div
            key={hazard.type}
            initial={{opacity: 0, y: 10}}
            animate={{opacity: 1, y: 0}}
            transition={{delay: index * 0.1}}
            className="bg-gray-50 rounded-lg p-4 text-center border border-gray-200"
          >
            <div className="text-3xl mb-2">{hazard.icon}</div>
            <h3 className="font-semibold text-gray-900 mb-1">{hazard.type}</h3>
            <div className="text-2xl font-bold text-gray-900 mb-2">{hazard.count}</div>
            
            {hazard.tideInfo && (
              <div className="text-xs text-gray-600 mb-2">
                <div className="flex items-center justify-center space-x-1">
                  <SafeIcon icon={FiClock} className="text-gray-500" />
                  <span>{hazard.tideInfo.time}</span>
                </div>
                <div className="mt-1">
                  {hazard.tideInfo.type === 'high_tide' ? 'High Tide' : 'Low Tide'}
                </div>
              </div>
            )}
            
            <div className="flex flex-col space-y-2 mt-2">
              {hazard.alerts > 0 && (
                <button 
                  onClick={() => onAlertClick && onAlertClick(hazard.alertEvents)}
                  className={`inline-flex items-center justify-center px-2 py-1 rounded-full text-xs font-medium ${hazard.color} hover:opacity-90 transition-opacity`}
                >
                  <SafeIcon icon={FiAlertTriangle} className="mr-1" />
                  {hazard.alerts} alert{hazard.alerts > 1 ? 's' : ''}
                  <SafeIcon icon={FiExternalLink} className="ml-1 text-xs" />
                </button>
              )}
              
              {hazard.count > 0 && (
                <button 
                  onClick={() => onShowEvents && onShowEvents(hazard.allEvents, hazard.type)}
                  className="inline-flex items-center justify-center px-2 py-1 rounded-full text-xs font-medium bg-gray-200 text-gray-800 hover:bg-gray-300 transition-colors"
                >
                  <SafeIcon icon={FiList} className="mr-1" />
                  View All
                </button>
              )}
            </div>
          </motion.div>
        ))}
      </div>
      
      <div className="mt-4 pt-4 border-t border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <SafeIcon icon={FiActivity} className="text-gray-500" />
            <span>Last 7 days</span>
          </div>
          <div className="text-sm text-gray-500">
            {iliganCalamities.length} total events in area
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default IliganScorecard;